import type { ConfirmationResult } from 'firebase/auth';

/** Wrong codes allowed for one SMS before a new code is needed. */
export const CODE_ATTEMPTS = 3;
/** Seconds before "Resend code" is offered. */
export const RESEND_AFTER_SEC = 30;
/** How long sign-in pauses after Firebase says "too many requests". */
export const PAUSE_MINUTES = 30;

export interface PendingSignIn {
  /** E.164, e.g. +919845012321. Kept in memory only. */
  phoneE164: string;
  confirmation: ConfirmationResult;
  sentAt: number;
  wrongAttempts: number;
}

// In memory on purpose: a confirmation can't be saved, and a reload should start again from the number.
let pending: PendingSignIn | null = null;

export const signInFlow = {
  get: () => pending,
  start(phoneE164: string, confirmation: ConfirmationResult) {
    pending = { phoneE164, confirmation, sentAt: Date.now(), wrongAttempts: 0 };
    return pending;
  },
  /** Counts a wrong code; returns how many attempts are left for this code. */
  recordWrongCode(): number {
    if (!pending) return 0;
    pending.wrongAttempts += 1;
    return Math.max(0, CODE_ATTEMPTS - pending.wrongAttempts);
  },
  clear() {
    pending = null;
  },
};

const PAUSE_KEY = 'oneapp:sign-in-paused-until';

/** When sign-in may be tried again on this phone, or null if it isn't paused. */
export function signInPausedUntil(now = Date.now()): Date | null {
  try {
    const until = Number(window.localStorage.getItem(PAUSE_KEY));
    return until > now ? new Date(until) : null;
  } catch {
    return null;
  }
}

export function pauseSignIn(now = Date.now()): Date {
  const until = new Date(now + PAUSE_MINUTES * 60_000);
  try {
    window.localStorage.setItem(PAUSE_KEY, String(until.getTime()));
  } catch {
    // storage blocked: the pause lasts for this screen only
  }
  return until;
}
